// FileUploadComponent.js - Komponente für das Hochladen von Dateien
import React from 'react';
import { useDropzone } from 'react-dropzone';

/**
 * @typedef {Object} FileUploadProps
 * @property {Function} onDrop - Callback, wenn eine Datei hochgeladen wird
 * @property {string} title - Titel des Upload-Bereichs
 * @property {string} description - Beschreibung des Upload-Bereichs
 * @property {string} acceptedFileTypes - Akzeptierte Dateitypen (z.B. '.xlsx')
 * @property {string|null} error - Fehlermeldung, falls vorhanden
 * @property {boolean} loading - Zeigt an, ob gerade geladen wird
 * @property {boolean} fileUploaded - Zeigt an, ob eine Datei hochgeladen wurde
 */

/**
 * Komponente für das Hochladen von Dateien per Drag & Drop
 * 
 * @param {FileUploadProps} props - Die Eigenschaften der Komponente
 */
const FileUploadComponent = ({ 
  onDrop, 
  title, 
  description, 
  acceptedFileTypes, 
  error, 
  loading, 
  fileUploaded 
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: { 
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    disabled: loading
  });

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      <div 
        {...getRootProps()} 
        className={`border-2 border-dashed p-8 rounded-lg text-center cursor-pointer transition
          ${isDragActive ? 'border-primary bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${loading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        {fileUploaded ? (
          <div className="text-green-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 font-medium">Datei erfolgreich geladen!</p>
          </div>
        ) : loading ? (
          <div className="text-gray-500">
            <svg className="animate-spin h-12 w-12 mx-auto text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-2 font-medium">Wird verarbeitet...</p>
          </div>
        ) : isDragActive ? (
          <div className="text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            <p className="mt-2 font-medium">Datei hier ablegen...</p>
          </div>
        ) : (
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="mt-2 text-gray-600">{description}</p>
            <p className="mt-1 text-sm text-gray-500">Akzeptierte Dateitypen: {acceptedFileTypes}</p>
          </div>
        )}
      </div>
      
      {error && (
        <div className="mt-2 text-red-600 text-sm">
          Fehler: {error}
        </div>
      )}
    </div>
  );
};

export default FileUploadComponent;
